import { useState, useRef, useEffect } from 'react';
import API from '@/lib/api';
import { X, UploadSimple, Sparkle, CheckCircle, ArrowsClockwise, Clipboard, DownloadSimple, Warning, ClockCounterClockwise, CaretDown, CaretRight, Buildings } from '@phosphor-icons/react';

const STATUS_META = {
  nieuw:      { label: '🆕 Nieuw',        color: '#065F46', bg: '#D1FAE5' },
  aangepast:  { label: '✎ Gewijzigd',    color: '#1E40AF', bg: '#DBEAFE' },
  controleer: { label: '⚠ Controleer',   color: '#92400E', bg: '#FEF3C7' },
  ongewijzigd:{ label: '= Ongewijzigd',  color: '#6B7280', bg: '#F3F4F6' },
};

/**
 * KlantImportModal — AI-powered klant-import met:
 *  - XLSX/CSV upload (auto-convert Excel → CSV)
 *  - AI kolom-herkenning (FR/DE/EN/PL headers)
 *  - Dubbel-detectie op BTW + e-mail
 *  - Validatie (BTW-formaat, e-mail-formaat)
 *  - Per-veld checkboxes voor bestaande klanten (kritieke velden: btwnr/email extra markeren)
 *  - Auto-backup + 1-klik rollback (20 bewaard per user)
 *  - Template-download (XLSX met 📘 handleiding-tab of CSV)
 */
export default function KlantImportModal({ open, onClose, onDone }) {
  const [step, setStep] = useState(1);
  const [csvText, setCsvText] = useState('');
  const [analyse, setAnalyse] = useState(null);
  const [filter, setFilter] = useState('alle');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [geaccepteerd, setGeaccepteerd] = useState({});
  const [expanded, setExpanded] = useState({});
  const [showBackups, setShowBackups] = useState(false);
  const [backups, setBackups] = useState([]);
  const [backupLabel, setBackupLabel] = useState('');
  const [autoVies, setAutoVies] = useState(true);
  const [previewPage, setPreviewPage] = useState(1);
  const fileRef = useRef(null);
  useEffect(() => { setPreviewPage(1); }, [filter, analyse]);

  useEffect(() => {
    if (!open) return;
    // reset op open
  }, [open]);

  const reset = () => {
    setStep(1); setCsvText(''); setAnalyse(null); setFilter('alle');
    setErr(''); setGeaccepteerd({}); setExpanded({}); setBackupLabel('');
  };

  const loadBackups = async () => {
    try { const r = await API.get('/klanten/import-backups'); setBackups(r.data?.backups || []); }
    catch { setBackups([]); }
  };

  const handleFile = async (file) => {
    if (!file || busy) return;
    setErr('');
    if (file.size > 20 * 1024 * 1024) { setErr('Bestand te groot: maximaal 20 MB.'); return; }
    const fname = (file.name || '').toLowerCase();
    if (fname.endsWith('.xlsx') || fname.endsWith('.xls')) {
      try {
        setBusy(true);
        const fd = new FormData();
        fd.append('file', file);
        const r = await API.post('/klanten/import-xlsx', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (r.data?.csv_text) setCsvText(r.data.csv_text);
        else setErr('Kon Excel-bestand niet lezen.');
      } catch (e) { setErr(e?.response?.data?.detail || 'Excel-upload mislukt'); }
      finally { setBusy(false); }
      return;
    }
    try { setCsvText(await file.text()); }
    catch { setErr('Bestand kon niet gelezen worden.'); }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  const downloadTemplate = async (format = 'xlsx') => {
    try {
      const resp = await API.get(`/klanten/import-template?format=${format}`, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url; a.download = `klant-import-template.${format}`; a.click();
      URL.revokeObjectURL(url);
    } catch { setErr('Template-download mislukt'); }
  };

  const analyseer = async () => {
    if (!csvText.trim()) { setErr('Plak of upload eerst CSV-data.'); return; }
    setBusy(true); setErr('');
    try {
      const r = await API.post('/klanten/import-analyse', { csv_text: csvText }, { timeout: 180000 });
      setAnalyse(r.data);
      setGeaccepteerd({}); setExpanded({});
      setStep(3);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Analyse mislukt');
    } finally { setBusy(false); }
  };

  const forceAI = async () => {
    setBusy(true); setErr('');
    try {
      const r = await API.post('/klanten/import-analyse', { csv_text: csvText, force_ai: true }, { timeout: 180000 });
      setAnalyse(r.data);
      setStep(3);
    } catch (e) { setErr(e?.response?.data?.detail || 'AI-analyse mislukt'); }
    finally { setBusy(false); }
  };

  const uitvoeren = async () => {
    if (!analyse) return;
    const buildItem = (r) => {
      const { huidig, veld_diffs, ...item } = r;
      if (r.status === 'aangepast' || r.status === 'controleer') {
        const perVeld = geaccepteerd[r.klant_id];
        if (perVeld) item.geaccepteerde_velden = Object.entries(perVeld).filter(([, v]) => v).map(([k]) => k);
      }
      return item;
    };
    const items = [...analyse.nieuwe, ...analyse.aangepaste, ...analyse.controleer].map(buildItem);
    const filtered = items.filter(it => {
      if (it.status === 'nieuw') return true;
      if (Array.isArray(it.geaccepteerde_velden) && it.geaccepteerde_velden.length === 0) return false;
      return true;
    });
    if (!filtered.length) { alert('Geen wijzigingen om door te voeren (vink minstens 1 veld aan).'); return; }
    const msg = `${analyse.nieuwe.length} nieuwe + ${analyse.aangepaste.length} bijgewerkte + ${analyse.controleer.length} ter controle = ${filtered.length} klanten importeren?\n\n💾 Automatische backup wordt gemaakt (1-klik herstel mogelijk).`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const r = await API.post('/klanten/import-uitvoeren', { items: filtered, backup_label: backupLabel || undefined, auto_vies: autoVies }, { timeout: 180000 });
      const bmsg = r.data.backup_id ? `\n🛡️ Backup-ID: ${r.data.backup_id}` : '';
      let viesInfo = '';
      if (r.data.vies) {
        if (r.data.vies.async) {
          viesInfo = `\n\n✨ VIES-verrijking loopt op de achtergrond voor ${r.data.vies.gepland} klant(en).\nDit duurt 1-3 minuten. Sluit deze modal en klik op "🔄 Vernieuwen" of ververs de pagina om de nieuwe bedrijfsnamen + adressen te zien.`;
        } else if (r.data.vies.gepoogd) {
          viesInfo = `\n✨ VIES: ${r.data.vies.verrijkt}/${r.data.vies.gepoogd} verrijkt${r.data.vies.ongeldig ? ` · ${r.data.vies.ongeldig} ongeldig` : ''}`;
        }
      }
      alert(`✓ ${r.data.ingevoegd} nieuwe + ${r.data.bijgewerkt} bijgewerkt${r.data.mislukt ? ` · ${r.data.mislukt} mislukt` : ''}.${bmsg}${viesInfo}`);
      onDone?.();
      reset();
      onClose();
    } catch (e) {
      const detail = e.response?.data?.detail || e?.message || 'Import mislukt';
      setErr('Import niet volledig bevestigd: ' + detail + '\nControleer de klantenlijst en analyseer het bestand opnieuw voordat je nogmaals importeert.');
    }
    finally { setBusy(false); }
  };

  const toggleVeld = (id, v, diffs) => {
    setGeaccepteerd(p => {
      const cur = p[id];
      let next;
      if (!cur) { next = {}; (diffs || []).forEach(d => { next[d.veld] = d.veld !== v; }); }
      else next = { ...cur, [v]: !cur[v] };
      return { ...p, [id]: next };
    });
  };
  const toggleAlle = (id, diffs, waarde) => {
    const next = {};
    diffs.forEach(d => { next[d.veld] = waarde; });
    setGeaccepteerd(p => ({ ...p, [id]: next }));
  };
  const isVeldOk = (id, v) => {
    const row = geaccepteerd[id];
    if (!row) return true;
    return row[v] !== false;
  };

  const openBackupsPanel = async () => { setShowBackups(true); await loadBackups(); };

  const herstelBackup = async (bid) => {
    if (!window.confirm('Backup herstellen?\n\nAlle klanten in deze backup worden teruggezet naar de snapshot-versie.')) return;
    setBusy(true);
    try {
      const r = await API.post(`/klanten/import-backups/${bid}/herstel`);
      alert(`✓ ${r.data.hersteld} klanten hersteld`);
      await loadBackups();
      onDone?.();
    } catch (e) { alert(e.response?.data?.detail || 'Herstel mislukt'); }
    finally { setBusy(false); }
  };

  if (!open) return null;

  const zichtbaar = analyse ? (
    filter === 'nieuw' ? analyse.nieuwe :
    filter === 'aangepast' ? analyse.aangepaste :
    filter === 'controleer' ? analyse.controleer :
    filter === 'ongewijzigd' ? analyse.ongewijzigd :
    [...analyse.nieuwe, ...analyse.aangepaste, ...analyse.controleer, ...analyse.ongewijzigd]
  ) : [];

  const getLabel = (r) => r.bedrijf || `${r.voorn || ''} ${r.achtern || ''}`.trim() || r.email || '—';
  const totalPreviewPages = Math.max(1, Math.ceil(zichtbaar.length / 100));
  const activePreviewPage = Math.min(previewPage, totalPreviewPages);
  const previewRows = zichtbaar.slice((activePreviewPage - 1) * 100, activePreviewPage * 100);
  const getInitials = (r) => {
    const src = r.bedrijf || `${r.voorn || ''}${r.achtern || ''}` || r.email || '?';
    return src.substring(0, 2).toUpperCase();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3" onClick={() => { if (!busy) onClose(); }} data-testid="klant-import-overlay">
      <div className="w-full max-w-6xl bg-white shadow-2xl flex flex-col" style={{ maxHeight: '92vh' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-3 bg-[#002FA7] text-white flex items-center justify-between">
          <h3 className="font-bold text-base inline-flex items-center gap-2">
            <Sparkle size={18} weight="fill" /> Klanten importeren
            <span className="text-[10px] bg-white/20 px-2 py-0.5 uppercase tracking-wider">Stap {step}/3</span>
          </h3>
          <div className="flex gap-1 items-center">
            <button onClick={openBackupsPanel} className="text-[11px] inline-flex items-center gap-1 px-2 py-1 hover:bg-white/10 rounded" data-testid="klant-import-open-backups" title="Klant-backups">
              <ClockCounterClockwise size={14} weight="duotone" /> Backups
            </button>
            <button onClick={onClose} disabled={busy} className="p-1.5 hover:bg-white/10" data-testid="klant-import-close"><X size={18} weight="bold" /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="px-5 pt-3 text-xs text-gray-600" data-testid="klant-import-limit">Maximaal 50.000 klanten per bestand · CSV of Excel · 20 MB</div>
          {err && <div className="mx-5 mt-3 p-2 text-xs whitespace-pre-line text-red-700 border border-red-300 bg-red-50" role="alert" data-testid="klant-import-error">{err}</div>}
          {/* STAP 1 — Upload */}
          {step === 1 && (
            <div className="p-5 space-y-3">
              <div className="text-sm text-gray-600 leading-relaxed">
                Upload een <b>Excel</b> (.xlsx), <b>CSV</b> of plak gegevens. Onze AI herkent automatisch kolom-namen in
                <b> 🇳🇱 Nederlands</b>, <b>🇫🇷 Frans</b> (Raison sociale, TVA…), <b>🇩🇪 Duits</b> (Firmenname, USt-IdNr), <b>🇬🇧 Engels</b> (Company, VAT) en <b>🇵🇱 Pools</b>.
              </div>

              <div
                className="border-2 border-dashed border-gray-300 p-8 text-center hover:border-[#002FA7] bg-gray-50 cursor-pointer transition-colors"
                onDragOver={e => e.preventDefault()}
                onDrop={onDrop}
                onClick={() => fileRef.current?.click()}
                data-testid="klant-import-drop"
              >
                <UploadSimple size={36} className="mx-auto text-gray-400 mb-2" weight="duotone" />
                <div className="text-sm font-bold text-gray-700">Sleep uw .xlsx / .csv bestand hierheen</div>
                <div className="text-[11px] text-gray-500 mt-1">✨ Excel-bestanden worden automatisch omgezet · of klik om te bladeren</div>
                <input ref={fileRef} type="file" accept=".csv,.txt,.tsv,.xlsx,.xls" className="hidden" onChange={e => handleFile(e.target.files?.[0])} data-testid="klant-import-file-input" />
              </div>

              <div className="flex items-center gap-2 justify-center text-xs pt-1">
                <span className="text-gray-500">Nieuw hier?</span>
                <button type="button" onClick={() => downloadTemplate('xlsx')} className="inline-flex items-center gap-1 text-[#002FA7] hover:underline font-bold" data-testid="klant-import-dl-template">
                  <DownloadSimple size={12} weight="bold" /> Download voorbeeld-Excel
                </button>
                <span className="text-gray-400">·</span>
                <button type="button" onClick={() => downloadTemplate('csv')} className="text-gray-600 hover:text-[#002FA7] hover:underline" data-testid="klant-import-dl-csv">of CSV</button>
              </div>

              <div className="relative">
                <textarea
                  value={csvText}
                  onChange={e => setCsvText(e.target.value)}
                  placeholder="…of plak CSV-data (semicolon of comma gescheiden)"
                  className="w-full h-40 px-3 py-2 text-xs border border-gray-300 font-mono focus:outline-none focus:border-[#002FA7]"
                  data-testid="klant-import-csv-textarea"
                />
                {csvText && (
                  <button onClick={() => setCsvText('')} className="absolute top-2 right-2 p-1 hover:bg-gray-100" title="Wis" aria-label="CSV wissen" data-testid="klant-import-clear">
                    <X size={14} />
                  </button>
                )}
              </div>

              <div className="flex gap-2 justify-end">
                <button onClick={() => setStep(2)} disabled={!csvText.trim()} className="btn btn-secondary btn-sm" data-testid="klant-import-step-preview">Voorbeeld</button>
                <button onClick={analyseer} disabled={busy || !csvText.trim()} className="btn btn-primary btn-sm inline-flex items-center gap-1" data-testid="klant-import-analyseer">
                  <Sparkle size={12} weight="fill" /> {busy ? 'Analyseren...' : 'AI Analyseer'}
                </button>
              </div>
            </div>
          )}

          {/* STAP 2 — CSV preview */}
          {step === 2 && (
            <div className="p-5 space-y-3">
              <div className="text-sm font-bold text-gray-700">CSV preview (eerste 20 rijen)</div>
              <pre className="bg-gray-50 p-3 text-[10px] font-mono overflow-auto max-h-80 border border-gray-200">
                {csvText.split('\n').slice(0, 20).join('\n')}
              </pre>
              <div className="flex gap-2 justify-between">
                <button onClick={() => setStep(1)} className="btn btn-secondary btn-sm" data-testid="klant-import-back">← Terug</button>
                <button onClick={analyseer} disabled={busy} className="btn btn-primary btn-sm inline-flex items-center gap-1">
                  <Sparkle size={12} weight="fill" /> Analyseer met AI
                </button>
              </div>
            </div>
          )}

          {/* STAP 3 — Resultaat */}
          {step === 3 && analyse && (
            <div className="p-5 space-y-4">
              {analyse.ai_mapping_used && analyse.mapping_info && (
                <div className="border-2 border-[#7C3AED]/30 bg-[#F5F3FF] p-3 text-xs text-[#4C1D95]">
                  <div className="font-bold inline-flex items-center gap-1 mb-1">
                    <Sparkle size={12} weight="fill" /> AI heeft uw CSV-kolommen automatisch gemapt
                  </div>
                  <div className="italic">{analyse.mapping_info.redenering || 'Kolommen geïdentificeerd op basis van inhoud en headers.'}</div>
                </div>
              )}
              {!analyse.ai_mapping_used && analyse.aangepaste.length === 0 && analyse.nieuwe.length < 2 && (
                <div className="text-xs">
                  <button onClick={forceAI} disabled={busy} className="text-[#7C3AED] hover:underline inline-flex items-center gap-1 font-bold" data-testid="klant-import-force-ai">
                    <Sparkle size={12} weight="fill" /> Weinig treffers — Probeer AI-kolomherkenning
                  </button>
                </div>
              )}

              {analyse.csv_duplicates?.length > 0 && (
                <div className="border-2 border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="klant-import-dup-banner">
                  <div className="font-bold inline-flex items-center gap-1 mb-1">
                    <Warning size={12} weight="fill" /> {analyse.csv_duplicates.length} duplicate(n) in CSV gedetecteerd
                  </div>
                  <div>Laatste rij per sleutel behouden. Controleer: {analyse.csv_duplicates.slice(0, 5).map(d => `${d.key}=${d.waarde}`).join(', ')}</div>
                </div>
              )}
              {analyse.validatie_fouten?.length > 0 && (
                <div className="border-2 border-red-300 bg-red-50 p-3 text-xs text-red-900" data-testid="klant-import-val-banner">
                  <div className="font-bold inline-flex items-center gap-1 mb-1">
                    <Warning size={12} weight="fill" /> {analyse.validatie_fouten.length} validatie-waarschuwing(en)
                  </div>
                  <ul className="list-disc ml-5 space-y-0.5">
                    {analyse.validatie_fouten.slice(0, 10).map((v, i) => (
                      <li key={i}><b>{v.klant}</b>: {v.veld} — {v.fout}{v.waarde ? ` (${v.waarde})` : ''}</li>
                    ))}
                    {analyse.validatie_fouten.length > 10 && <li className="italic">+ {analyse.validatie_fouten.length - 10} meer…</li>}
                  </ul>
                </div>
              )}

              <div className="grid grid-cols-4 gap-2">
                <KPI label="Nieuw" count={analyse.nieuwe.length} color="#065F46" bg="#D1FAE5" active={filter === 'nieuw'} onClick={() => setFilter('nieuw')} />
                <KPI label="Gewijzigd" count={analyse.aangepaste.length} color="#1E40AF" bg="#DBEAFE" active={filter === 'aangepast'} onClick={() => setFilter('aangepast')} />
                <KPI label="Controleer" count={analyse.controleer.length} color="#92400E" bg="#FEF3C7" active={filter === 'controleer'} onClick={() => setFilter('controleer')} />
                <KPI label="Ongewijzigd" count={analyse.ongewijzigd.length} color="#6B7280" bg="#F3F4F6" active={filter === 'ongewijzigd'} onClick={() => setFilter('ongewijzigd')} />
              </div>
              <div className="flex gap-2 justify-between items-center">
                <button onClick={() => setFilter('alle')} className={`text-xs px-2 py-1 ${filter === 'alle' ? 'bg-[#002FA7] text-white' : 'bg-gray-100 text-gray-600'}`} data-testid="klant-import-filter-all">Alle ({analyse.totaal})</button>
                <span className="text-[10px] text-gray-500 italic">💡 Klik op rij om per-veld wijzigingen te bekijken</span>
              </div>

              <div className="border border-gray-200 overflow-auto max-h-[26rem]">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr className="text-[10px] uppercase tracking-wider text-gray-600 font-bold">
                      <th className="text-center px-2 py-1.5 w-6"></th>
                      <th className="text-center px-2 py-1.5 w-12"></th>
                      <th className="text-left px-2 py-1.5">Bedrijf/Naam</th>
                      <th className="text-left px-2 py-1.5">BTW-nr</th>
                      <th className="text-left px-2 py-1.5">E-mail</th>
                      <th className="text-left px-2 py-1.5">Stad</th>
                      <th className="text-left px-2 py-1.5">Land</th>
                      <th className="text-left px-2 py-1.5">Status / Velden</th>
                    </tr>
                  </thead>
                  <tbody>
                    {zichtbaar.length === 0 ? (
                      <tr><td colSpan={8} className="text-center py-6 text-gray-400 italic">Geen items in deze categorie.</td></tr>
                    ) : previewRows.map((r, offset) => {
                      const i = (activePreviewPage - 1) * 100 + offset;
                      const meta = STATUS_META[r.status] || STATUS_META.ongewijzigd;
                      const heeftDiffs = (r.veld_diffs || []).length > 0;
                      const rowId = r.klant_id || `new-${i}`;
                      const isOpen = expanded[rowId];
                      const acceptedCount = (r.veld_diffs || []).filter(d => isVeldOk(rowId, d.veld)).length;
                      return [
                        <tr key={`row-${rowId}`} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`klant-import-rij-${i}`}>
                          <td className="px-2 py-1.5 text-center">
                            {heeftDiffs && (
                              <button onClick={() => setExpanded(p => ({ ...p, [rowId]: !p[rowId] }))} className="text-gray-400 hover:text-[#002FA7]" data-testid={`klant-import-toggle-${i}`}>
                                {isOpen ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
                              </button>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-center">
                            {r.status === 'nieuw' ? (
                              <div className="w-8 h-8 bg-emerald-100 border border-emerald-300 rounded-full flex items-center justify-center text-emerald-700 font-bold text-xs">+</div>
                            ) : (
                              <div className="w-8 h-8 bg-blue-100 border border-blue-200 rounded-full flex items-center justify-center text-blue-700 font-bold text-[10px]" title={getLabel(r)}>
                                {getInitials(r)}
                              </div>
                            )}
                          </td>
                          <td className="px-2 py-1.5 font-bold truncate max-w-[200px]">
                            <div className="flex items-center gap-1">
                              <Buildings size={10} className="text-gray-400" />{getLabel(r)}
                            </div>
                          </td>
                          <td className="px-2 py-1.5 font-mono text-[11px]">{r.btwnr || '—'}</td>
                          <td className="px-2 py-1.5 truncate max-w-[160px]">{r.email || '—'}</td>
                          <td className="px-2 py-1.5">{r.stad || '—'}</td>
                          <td className="px-2 py-1.5">{r.land || '—'}</td>
                          <td className="px-2 py-1.5">
                            <span className="inline-block px-1.5 py-0.5 font-bold text-[10px] mr-1" style={{ background: meta.bg, color: meta.color }}>{meta.label}</span>
                            {heeftDiffs && (<span className="text-[10px] text-gray-600">{acceptedCount}/{r.veld_diffs.length} ✓</span>)}
                          </td>
                        </tr>,
                        isOpen && heeftDiffs ? (
                          <tr key={`exp-${rowId}`} className="border-t border-gray-100 bg-blue-50/30">
                            <td colSpan={8} className="px-4 py-3">
                              <div className="flex items-center justify-between mb-2">
                                <div className="text-[11px] font-bold text-gray-700">📝 Wijzigingen per veld — vink aan wat je wil doorvoeren</div>
                                <div className="flex gap-2">
                                  <button className="text-[10px] text-[#002FA7] hover:underline" onClick={() => toggleAlle(rowId, r.veld_diffs, true)} data-testid={`klant-sel-all-${i}`}>Alles aan</button>
                                  <span className="text-gray-300">|</span>
                                  <button className="text-[10px] text-red-600 hover:underline" onClick={() => toggleAlle(rowId, r.veld_diffs, false)} data-testid={`klant-sel-none-${i}`}>Alles uit</button>
                                </div>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                                {(r.veld_diffs || []).map((d, idx) => {
                                  const checked = isVeldOk(rowId, d.veld);
                                  const isKritiek = d.is_kritiek;
                                  return (
                                    <label key={idx} className={`flex items-center gap-2 px-2 py-1.5 border rounded cursor-pointer text-[11px] ${
                                      checked
                                        ? isKritiek ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'
                                        : 'border-gray-200 bg-gray-50 opacity-60'
                                    }`} data-testid={`klant-veld-${i}-${d.veld}`}>
                                      <input type="checkbox" checked={checked} onChange={() => toggleVeld(rowId, d.veld, r.veld_diffs)} className="w-3.5 h-3.5" />
                                      <div className="flex-1">
                                        <div className="font-bold text-gray-700">{d.label} {isKritiek && <span className="text-red-600">⚠ Kritiek</span>}</div>
                                        <div className="text-gray-600 font-mono text-[10px]">
                                          <span className="line-through text-red-500">{d.oud || '—'}</span>
                                          <span className="mx-1 text-gray-400">→</span>
                                          <span className="text-emerald-700 font-bold">{d.nieuw}</span>
                                        </div>
                                      </div>
                                    </label>
                                  );
                                })}
                              </div>
                            </td>
                          </tr>
                        ) : null
                      ];
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-wrap justify-between items-center gap-2 text-xs" data-testid="klant-import-pagination">
                <span data-testid="klant-import-preview-count">{zichtbaar.length ? (activePreviewPage - 1) * 100 + 1 : 0}–{Math.min(activePreviewPage * 100, zichtbaar.length)} van {zichtbaar.length.toLocaleString('nl-BE')}</span>
                <div className="flex flex-wrap items-center gap-1">
                  <button type="button" disabled={activePreviewPage === 1 || busy} onClick={() => setPreviewPage(1)} className="btn btn-ghost btn-sm" data-testid="klant-import-page-first">«</button>
                  <button type="button" disabled={activePreviewPage === 1 || busy} onClick={() => setPreviewPage(p => p - 1)} className="btn btn-ghost btn-sm" data-testid="klant-import-page-prev">Vorige</button>
                  <span data-testid="klant-import-page-number">{activePreviewPage} / {totalPreviewPages}</span>
                  <button type="button" disabled={activePreviewPage === totalPreviewPages || busy} onClick={() => setPreviewPage(p => p + 1)} className="btn btn-ghost btn-sm" data-testid="klant-import-page-next">Volgende</button>
                  <button type="button" disabled={activePreviewPage === totalPreviewPages || busy} onClick={() => setPreviewPage(totalPreviewPages)} className="btn btn-ghost btn-sm" data-testid="klant-import-page-last">»</button>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <label className="text-[11px] font-bold text-gray-600 whitespace-nowrap">🛡️ Backup-label:</label>
                <input type="text" value={backupLabel} onChange={e => setBackupLabel(e.target.value)} placeholder="Bijv. 'Klant-import maart 2026'" className="field-input text-xs flex-1" data-testid="klant-import-backup-label" />
              </div>
              <label className={`flex items-center gap-2 p-2 border cursor-pointer ${autoVies ? 'border-emerald-300 bg-emerald-50' : 'border-gray-300 bg-gray-50'}`} data-testid="klant-import-auto-vies-label">
                <input type="checkbox" checked={autoVies} onChange={e => setAutoVies(e.target.checked)} className="w-4 h-4" data-testid="klant-import-auto-vies" />
                <div className="flex-1 text-[11px]">
                  <div className="font-bold text-gray-800">
                    <Sparkle size={12} weight="fill" className="inline text-emerald-600" /> Automatische VIES-verrijking
                  </div>
                  <div className="text-gray-600">
                    Bij elke import-rij met BTW-nummer wordt automatisch de bedrijfsnaam + straat + postcode + stad opgehaald via VIES (gratis, EU-officieel).
                    <span className="text-gray-500"> Lege velden worden ingevuld; bestaande waarden blijven behouden.</span>
                  </div>
                </div>
              </label>

              <div className="flex gap-2 justify-between">
                <button onClick={() => setStep(1)} disabled={busy} className="btn btn-secondary btn-sm inline-flex items-center gap-1" data-testid="klant-import-restart"><ArrowsClockwise size={12} /> Opnieuw</button>
                <button onClick={uitvoeren} disabled={busy || (analyse.nieuwe.length + analyse.aangepaste.length + analyse.controleer.length === 0)} className="btn btn-primary font-bold inline-flex items-center gap-2" data-testid="klant-import-toepassen">
                  <CheckCircle size={14} weight="fill" /> {busy ? 'Bezig...' : `Toepassen (${analyse.nieuwe.length + analyse.aangepaste.length + analyse.controleer.length})`}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Backup-historiek */}
      {showBackups && (
        <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-3" onClick={() => setShowBackups(false)}>
          <div className="w-full max-w-3xl bg-white shadow-2xl flex flex-col" style={{ maxHeight: '85vh' }} onClick={e => e.stopPropagation()}>
            <div className="px-5 py-3 bg-[#002FA7] text-white flex items-center justify-between">
              <h3 className="font-bold text-base inline-flex items-center gap-2">
                <ClockCounterClockwise size={18} weight="fill" /> Klant-import-backups
              </h3>
              <button onClick={() => setShowBackups(false)} className="p-1.5 hover:bg-white/10" aria-label="Back-ups sluiten" data-testid="klant-import-backups-close"><X size={18} weight="bold" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {backups.length === 0 ? (
                <div className="text-center text-gray-400 italic py-8">Nog geen klant-import-backups.</div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr className="text-[10px] uppercase tracking-wider text-gray-600 font-bold">
                      <th className="text-left px-2 py-1.5">Datum</th>
                      <th className="text-left px-2 py-1.5">Label</th>
                      <th className="text-right px-2 py-1.5">Gewijzigd</th>
                      <th className="text-right px-2 py-1.5">Nieuw</th>
                      <th className="text-center px-2 py-1.5">Status</th>
                      <th className="text-center px-2 py-1.5">Actie</th>
                    </tr>
                  </thead>
                  <tbody>
                    {backups.map(b => {
                      const hersteld = b.status === 'hersteld';
                      const dt = b.timestamp ? new Date(b.timestamp).toLocaleString('nl-BE') : '';
                      return (
                        <tr key={b.id} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="px-2 py-2 font-mono text-[11px]">{dt}</td>
                          <td className="px-2 py-2">{b.label || '—'}</td>
                          <td className="px-2 py-2 text-right font-mono">{b.aantal_gewijzigd}</td>
                          <td className="px-2 py-2 text-right font-mono">{b.aantal_nieuw || 0}</td>
                          <td className="px-2 py-2 text-center">
                            {hersteld
                              ? <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold bg-gray-100 text-gray-500">Hersteld</span>
                              : <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold bg-emerald-50 text-emerald-700">Actief</span>}
                          </td>
                          <td className="px-2 py-2 text-center">
                            <button onClick={() => herstelBackup(b.id)} disabled={hersteld || busy} className="btn btn-secondary btn-sm text-[10px]" data-testid={`klant-backup-herstel-${b.id}`}>↺ Herstel</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KPI({ label, count, color, bg, active, onClick }) {
  return (
    <button onClick={onClick} className={`p-3 text-left transition-all ${active ? 'ring-2 ring-[#002FA7]' : ''}`} style={{ background: bg }} data-testid={`klant-kpi-${label.toLowerCase()}`}>
      <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color }}>{label}</div>
      <div className="text-2xl font-bold" style={{ color }}>{count}</div>
    </button>
  );
}
